import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Hls from 'hls.js';
import { fetchCategories, createCategory, updateCategory, deleteCategory } from '../services/categoriesService';
import {
  fetchTrainingVideos,
  fetchTrainingVideoStatus,
  fetchTrainingPlayback,
  uploadTrainingVideoXhr,
  patchTrainingVideo,
  deleteTrainingVideo,
} from '../services/trainingVideosService';
import { searchContentItems } from '../services/productsService';
import { getApiBaseUrl } from '../services/apiClient';
import { useConfirmDialog } from '../contexts/ConfirmContext';
import './TrainingVideosPage.css';

function normId(id) {
  if (!id) return null;
  return String(id);
}

const TUR_BILGI = {
  video: { ad: 'Video', ikon: 'fa-video', hazirlikBaslik: 'Sunuma dönüştürülüyor (HLS)' },
  pdf: { ad: 'PDF', ikon: 'fa-file-pdf', hazirlikBaslik: 'Sayfalar hazırlanıyor' },
  gorsel: { ad: 'Görsel', ikon: 'fa-image', hazirlikBaslik: 'Görsel işleniyor' },
};

function turBilgisi(tur) {
  return TUR_BILGI[tur] || TUR_BILGI.video;
}

/** Sunucu türü kendisi çıkarır; buradaki tespit yalnızca form metinleri için */
function dosyaTuruBelirle(file) {
  if (!file) return null;
  const mime = (file.type || '').toLowerCase();
  const ad = (file.name || '').toLowerCase();
  if (mime === 'application/pdf' || ad.endsWith('.pdf')) return 'pdf';
  if (mime.startsWith('image/') || /\.(jpe?g|png|webp)$/.test(ad)) return 'gorsel';
  if (mime.startsWith('video/') || /\.(mp4|mov|webm|avi|mkv)$/.test(ad)) return 'video';
  return null;
}

function buildCategoryTree(categories) {
  const map = {};
  const roots = [];
  (categories || []).forEach((c) => {
    const id = normId(c.id || c._id);
    if (!id) return;
    map[id] = { ...c, id, children: [] };
  });
  (categories || []).forEach((c) => {
    const id = normId(c.id || c._id);
    if (!id || !map[id]) return;
    const pid = normId(c.ustKategoriId);
    if (pid && map[pid]) map[pid].children.push(map[id]);
    else roots.push(map[id]);
  });
  const sortN = (a, b) => (a.kategoriSirasi || 0) - (b.kategoriSirasi || 0);
  const walk = (nodes) => {
    nodes.sort(sortN);
    nodes.forEach((n) => walk(n.children || []));
  };
  walk(roots);
  return roots;
}

function ContentTagPicker({ icerikTipi, label, value, displayName, onChange }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);

  const search = useCallback(async () => {
    if (!q || q.trim().length < 3) {
      setResults([]);
      return;
    }
    setBusy(true);
    try {
      const res = await searchContentItems(q.trim(), { icerikTipi });
      const items = res?.veri?.icerikler || res?.veri?.items || res?.veri || [];
      const list = Array.isArray(items) ? items : [];
      setResults(
        list.map((it) => ({
          id: String(it._id || it.id || it.icerikId || ''),
          ad: it.icerikAdi || it.ad || it.parcaAdi || it.islemAdi || it.arizaAdi || '—',
        }))
      );
    } catch {
      setResults([]);
    } finally {
      setBusy(false);
    }
  }, [q, icerikTipi]);

  const pick = (id, ad) => {
    onChange(id, ad);
    setResults([]);
    setQ('');
  };

  return (
    <div className="form-field full-width">
      <span>
        {label}{' '}
        <span className="tag-picker-hint">(opsiyonel, en az 3 karakter ara)</span>
      </span>
      <div className="tag-search-row">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ara…" />
        <button type="button" className="secondary-btn" onClick={search} disabled={busy}>
          Ara
        </button>
      </div>
      {value && (
        <div className="tv-tag-selected">
          Seçili: <strong>{displayName || value}</strong>{' '}
          <button type="button" className="secondary-btn tv-tag-remove-btn" onClick={() => onChange('', '')}>
            Kaldır
          </button>
        </div>
      )}
      {!value && results.length > 0 && (
        <div className="tag-results">
          {results.map((r) => (
            <div
              key={r.id}
              className="tag-result-item"
              role="button"
              tabIndex={0}
              onClick={() => pick(r.id, r.ad)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') pick(r.id, r.ad);
              }}
            >
              {r.ad}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function TrainingVideosPage() {
  const { confirm } = useConfirmDialog();
  const [categories, setCategories] = useState([]);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  const [addOpen, setAddOpen] = useState(false);
  const [addKategoriId, setAddKategoriId] = useState('');
  const [baslik, setBaslik] = useState('');
  const [icerik, setIcerik] = useState('');
  const [file, setFile] = useState(null);
  const [uploadPct, setUploadPct] = useState(0);
  const [phase, setPhase] = useState('');
  const [activeJobId, setActiveJobId] = useState(null);
  const [transPct, setTransPct] = useState(0);

  const [tagUrun, setTagUrun] = useState({ id: '', ad: '' });
  const [tagParca, setTagParca] = useState({ id: '', ad: '' });
  const [tagIslem, setTagIslem] = useState({ id: '', ad: '' });
  const [tagAriza, setTagAriza] = useState({ id: '', ad: '' });

  const [playOpen, setPlayOpen] = useState(false);
  const [playUrl, setPlayUrl] = useState('');
  const [playTur, setPlayTur] = useState('video');
  const [playSayfalar, setPlaySayfalar] = useState([]);
  const videoRef = useRef(null);

  const [catOpen, setCatOpen] = useState(false);
  const [catMode, setCatMode] = useState('create');
  const [catName, setCatName] = useState('');
  const [catParentId, setCatParentId] = useState('');
  const [catEditId, setCatEditId] = useState('');
  const [catError, setCatError] = useState('');
  const [catSubmitting, setCatSubmitting] = useState(false);

  const [editOpen, setEditOpen] = useState(false);
  const [editId, setEditId] = useState('');
  const [editBaslik, setEditBaslik] = useState('');
  const [editIcerik, setEditIcerik] = useState('');
  const [editKategoriId, setEditKategoriId] = useState('');
  const [editError, setEditError] = useState('');
  const [editSubmitting, setEditSubmitting] = useState(false);

  const secilenTur = useMemo(() => dosyaTuruBelirle(file), [file]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setErr('');
    try {
      const [catRes, vidRes] = await Promise.all([
        fetchCategories({ kategoriTipi: 'egitim' }),
        fetchTrainingVideos(),
      ]);
      const kats = catRes?.veri?.kategoriler ?? catRes?.kategoriler ?? [];
      const vids = vidRes?.veri?.videolar ?? vidRes?.videolar ?? [];
      setCategories(Array.isArray(kats) ? kats : []);
      setVideos(Array.isArray(vids) ? vids : []);
    } catch (e) {
      setErr(e?.message || 'Veri yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const categoryById = useMemo(() => {
    const m = {};
    (categories || []).forEach((c) => {
      const id = normId(c.id || c._id);
      if (id) m[id] = { ...c, id };
    });
    return m;
  }, [categories]);

  const categoryTitleMap = useMemo(() => {
    const m = {};
    Object.entries(categoryById).forEach(([id, c]) => {
      m[id] = c.kategoriAdi || id;
    });
    return m;
  }, [categoryById]);

  const parentCategoryOptions = useMemo(() => {
    const tree = buildCategoryTree(categories);
    const out = [];
    const walk = (nodes, path) => {
      (nodes || []).forEach((n) => {
        const p = [...path, n.kategoriAdi];
        out.push({ id: n.id, label: p.join(' › ') });
        if (n.children && n.children.length) walk(n.children, p);
      });
    };
    walk(tree, []);
    return out;
  }, [categories]);

  const flatCategoryOptions = useMemo(() => {
    const tree = buildCategoryTree(categories);
    const out = [];
    const walk = (nodes, path) => {
      (nodes || []).forEach((n) => {
        const p = [...path, n.kategoriAdi];
        if (!n.children || n.children.length === 0) {
          out.push({ id: n.id, label: p.join(' › ') });
        } else walk(n.children, p);
      });
    };
    walk(tree, []);
    return out;
  }, [categories]);

  const videosByKey = useMemo(() => {
    const m = {};
    (videos || []).forEach((v) => {
      const key = normId(v.kategoriId) || '_none';
      if (!m[key]) m[key] = [];
      m[key].push(v);
    });
    return m;
  }, [videos]);

  /** Kategorisiz + tüm yaprak eğitim kategorileri (video olmasa da); videolarda kullanılan ama yaprak listesinde olmayan id’ler ayrı bölüm. */
  const displaySections = useMemo(() => {
    const leafIds = new Set(flatCategoryOptions.map((o) => o.id));
    const list = [];

    list.push({
      key: '_none',
      kategoriId: '',
      title: 'Kategorisiz',
      isOrphan: false,
      items: videosByKey._none || [],
    });

    const leafSorted = [...flatCategoryOptions].sort((a, b) => a.label.localeCompare(b.label, 'tr'));
    leafSorted.forEach((o) => {
      list.push({
        key: o.id,
        kategoriId: o.id,
        title: o.label,
        isOrphan: false,
        items: videosByKey[o.id] || [],
      });
    });

    const orphanKeys = Object.keys(videosByKey).filter((k) => k !== '_none' && !leafIds.has(k));
    orphanKeys.sort((a, b) => {
      const ta = categoryTitleMap[a] || a;
      const tb = categoryTitleMap[b] || b;
      return String(ta).localeCompare(String(tb), 'tr');
    });
    orphanKeys.forEach((k) => {
      list.push({
        key: `orphan-${k}`,
        kategoriId: k,
        title: categoryTitleMap[k] ? `${categoryTitleMap[k]} (yaprak değil veya silinmiş)` : `Kategori #${k}`,
        isOrphan: true,
        items: videosByKey[k] || [],
      });
    });

    return list;
  }, [videosByKey, flatCategoryOptions, categoryTitleMap]);

  const selectedCategoryLabel = useMemo(() => {
    if (!addKategoriId) return 'Kategorisiz';
    const o = flatCategoryOptions.find((x) => x.id === addKategoriId);
    return o?.label || categoryTitleMap[addKategoriId] || addKategoriId;
  }, [addKategoriId, flatCategoryOptions, categoryTitleMap]);

  useEffect(() => {
    if (!activeJobId || phase !== 'transcode') return undefined;
    const t = setInterval(async () => {
      try {
        const res = await fetchTrainingVideoStatus(activeJobId);
        const d = res?.veri;
        if (!d) return;
        setTransPct(d.islemYuzdesi ?? 0);
        if (d.durum === 'hazir') {
          setPhase('done');
          setActiveJobId(null);
          loadAll();
        }
        if (d.durum === 'hata') {
          setPhase('');
          setErr(d.hataMesaji || 'Dönüştürme hatası');
          setActiveJobId(null);
        }
      } catch (_) {
        /* ignore */
      }
    }, 1500);
    return () => clearInterval(t);
  }, [activeJobId, phase, loadAll]);

  useEffect(() => {
    if (!playOpen || playTur !== 'video' || !playUrl || !videoRef.current) return undefined;
    const video = videoRef.current;
    if (Hls.isSupported()) {
      const hls = new Hls({ enableWorker: true });
      hls.loadSource(playUrl);
      hls.attachMedia(video);
      hls.on(Hls.Events.ERROR, (_, data) => {
        if (data.fatal) console.warn('HLS fatal', data);
      });
      return () => {
        hls.destroy();
      };
    }
    video.src = playUrl;
    return undefined;
  }, [playOpen, playUrl, playTur]);

  const resetTagState = () => {
    setTagUrun({ id: '', ad: '' });
    setTagParca({ id: '', ad: '' });
    setTagIslem({ id: '', ad: '' });
    setTagAriza({ id: '', ad: '' });
  };

  const openAddModal = (kategoriIdPref) => {
    setBaslik('');
    setIcerik('');
    setFile(null);
    setUploadPct(0);
    setPhase('');
    resetTagState();
    setAddKategoriId(kategoriIdPref || '');
    setAddOpen(true);
  };

  const submitUpload = async (e) => {
    e.preventDefault();
    if (!baslik.trim()) return;
    if (!file) return;
    setErr('');
    const fd = new FormData();
    fd.append('dosya', file);
    fd.append('baslik', baslik.trim());
    fd.append('icerik', icerik || '');
    if (addKategoriId) fd.append('kategoriId', addKategoriId);
    if (tagUrun.id) fd.append('urunIcerikId', tagUrun.id);
    if (tagParca.id) fd.append('parcaIcerikId', tagParca.id);
    if (tagIslem.id) fd.append('islemIcerikId', tagIslem.id);
    if (tagAriza.id) fd.append('arizaIcerikId', tagAriza.id);

    setPhase('upload');
    setUploadPct(0);
    try {
      const res = await uploadTrainingVideoXhr(fd, (p) => setUploadPct(p));
      const id = res?.veri?.video?.id;
      if (id) {
        setPhase('transcode');
        setActiveJobId(id);
        setTransPct(0);
      } else {
        setPhase('done');
        loadAll();
      }
    } catch (ex) {
      setErr(ex?.message || 'Yükleme başarısız');
      setPhase('');
    }
  };

  const mutlakUrl = (rel) => (rel.startsWith('http') ? rel : `${getApiBaseUrl()}${rel}`);

  const openPlay = async (id) => {
    try {
      const res = await fetchTrainingPlayback(id);
      const veri = res?.veri || {};
      const tur = veri.tur || 'video';

      if (tur === 'pdf') {
        const sayfalar = Array.isArray(veri.sayfalar) ? veri.sayfalar : [];
        if (sayfalar.length === 0) throw new Error('Sayfa görselleri yok');
        setPlaySayfalar(sayfalar.map(mutlakUrl));
        setPlayUrl('');
      } else if (tur === 'gorsel') {
        if (!veri.gorselUrl) throw new Error('Görsel bağlantısı yok');
        setPlaySayfalar([]);
        setPlayUrl(mutlakUrl(veri.gorselUrl));
      } else {
        const rel = veri.manifestUrl || veri.manifestPath;
        if (!rel) throw new Error('Oynatma bağlantısı yok');
        setPlaySayfalar([]);
        setPlayUrl(mutlakUrl(rel));
      }

      setPlayTur(tur);
      setPlayOpen(true);
    } catch (ex) {
      setErr(ex?.message || 'Önizlenemedi');
    }
  };

  const openEdit = (item) => {
    setEditId(item.id);
    setEditBaslik(item.baslik || '');
    setEditIcerik(item.icerik || '');
    setEditKategoriId(normId(item.kategoriId) || '');
    setEditError('');
    setEditOpen(true);
  };

  const closeEdit = () => {
    if (editSubmitting) return;
    setEditOpen(false);
  };

  const submitEdit = async (e) => {
    e.preventDefault();
    const ad = editBaslik.trim();
    if (!ad) {
      setEditError('Başlık zorunludur.');
      return;
    }
    setEditSubmitting(true);
    setEditError('');
    try {
      const res = await patchTrainingVideo(editId, {
        baslik: ad,
        icerik: editIcerik,
        kategoriId: editKategoriId || null,
      });
      if (res && res.basarili === false) throw new Error(res.mesaj || 'Güncellenemedi');
      setEditOpen(false);
      await loadAll();
    } catch (ex) {
      setEditError(ex?.message || 'Güncellenemedi');
    } finally {
      setEditSubmitting(false);
    }
  };

  const handleDelete = async (id, tur) => {
    const ad = turBilgisi(tur).ad.toLowerCase();
    const ok = await confirm({
      title: 'Materyali sil',
      message: `Bu eğitim ${ad} materyalini silmek istediğinize emin misiniz?`,
      variant: 'danger',
      confirmLabel: 'Evet, sil',
    });
    if (!ok) return;
    try {
      await deleteTrainingVideo(id);
      loadAll();
    } catch (ex) {
      setErr(ex?.message || 'Silinemedi');
    }
  };

  const closeAdd = () => {
    if (phase === 'upload' || phase === 'transcode') return;
    setAddOpen(false);
  };

  const openCreateCategory = () => {
    setCatMode('create');
    setCatName('');
    setCatParentId('');
    setCatEditId('');
    setCatError('');
    setCatOpen(true);
  };

  const openEditCategory = (category) => {
    const id = normId(category.id || category._id);
    setCatMode('edit');
    setCatName(category.kategoriAdi || '');
    setCatParentId('');
    setCatEditId(id || '');
    setCatError('');
    setCatOpen(true);
  };

  const closeCatModal = () => {
    if (catSubmitting) return;
    setCatOpen(false);
  };

  const submitCategory = async (e) => {
    e.preventDefault();
    const ad = catName.trim();
    if (!ad) {
      setCatError('Kategori adı zorunludur.');
      return;
    }
    setCatSubmitting(true);
    setCatError('');
    try {
      if (catMode === 'create') {
        const payload = { kategoriAdi: ad, kategoriTipi: 'egitim' };
        if (catParentId) payload.ustKategoriId = catParentId;
        const res = await createCategory(payload);
        if (res && res.basarili === false) throw new Error(res.mesaj || 'Kategori eklenemedi');
      } else {
        const res = await updateCategory(catEditId, { kategoriAdi: ad });
        if (res && res.basarili === false) throw new Error(res.mesaj || 'Kategori güncellenemedi');
      }
      setCatOpen(false);
      await loadAll();
    } catch (ex) {
      setCatError(ex?.message || 'Kategori kaydedilemedi');
    } finally {
      setCatSubmitting(false);
    }
  };

  const handleDeleteCategory = async (category) => {
    const id = normId(category.id || category._id);
    if (!id) return;
    const ad = category.kategoriAdi || 'Kategori';
    const ok = await confirm({
      title: 'Kategoriyi sil',
      message: `"${ad}" silinecek. Alt kategoriler bir üst gruba bağlanır, bağlı içerikler silinmez. Devam edilsin mi?`,
      variant: 'danger',
      confirmLabel: 'Evet, sil',
    });
    if (!ok) return;
    try {
      const res = await deleteCategory(id);
      if (res && res.basarili === false) throw new Error(res.mesaj || 'Kategori silinemedi');
      await loadAll();
    } catch (ex) {
      setErr(ex?.message || 'Kategori silinemedi');
    }
  };

  return (
    <main className="main-content training-videos-page">
      <div className="content-header">
        <h1>Eğitimler</h1>
      </div>

      {err && <div className="tv-page-alert tv-page-alert--error">{err}</div>}

      {loading ? (
        <p>Yükleniyor…</p>
      ) : (
        <>
          <div className="tv-page-toolbar">
            <button type="button" className="primary-btn" onClick={openCreateCategory}>
              <i className="fas fa-plus" aria-hidden="true" />
              <span>Kategori ekle</span>
            </button>
            <button
              type="button"
              className="tv-icon-btn tv-icon-btn--primary tv-icon-btn--lg"
              onClick={() => openAddModal('')}
              title="Yeni materyal ekle (kategorisiz)"
              aria-label="Yeni materyal ekle (kategorisiz)"
            >
              <span className="tv-icon-btn__duo" aria-hidden="true">
                <i className="fa-solid fa-play" />
                <i className="fa-solid fa-plus tv-icon-btn__duo-plus" />
              </span>
            </button>
          </div>

          {displaySections.map((sec) => (
            <div key={sec.key} className="tv-section">
              <div className="tv-section-header">
                <h2>
                  {sec.title}
                  <span className="tv-section-badge">{sec.items.length} materyal</span>
                  {sec.isOrphan && (
                    <span className="tv-section-badge" title="Materyal, geçerli yaprak kategoriyle eşleşmiyor">
                      uyarı
                    </span>
                  )}
                </h2>
                <div className="tv-section-actions">
                  {sec.kategoriId && categoryById[sec.kategoriId] && (
                    <>
                      <button
                        type="button"
                        className="tv-icon-btn tv-icon-btn--ghost tv-icon-btn--md"
                        onClick={() => openEditCategory(categoryById[sec.kategoriId])}
                        title="Kategori adını düzenle"
                        aria-label={`Düzenle: ${sec.title}`}
                      >
                        <i className="fa-solid fa-pen" aria-hidden="true" />
                      </button>
                      <button
                        type="button"
                        className="tv-icon-btn tv-icon-btn--danger tv-icon-btn--md"
                        onClick={() => handleDeleteCategory(categoryById[sec.kategoriId])}
                        title="Kategoriyi sil"
                        aria-label={`Sil: ${sec.title}`}
                      >
                        <i className="fa-solid fa-trash-can" aria-hidden="true" />
                      </button>
                    </>
                  )}
                  <button
                    type="button"
                    className="tv-icon-btn tv-icon-btn--primary tv-icon-btn--md"
                    onClick={() => openAddModal(sec.kategoriId || '')}
                    title={`Bu kategoriye materyal ekle: ${sec.title}`}
                    aria-label={`Bu kategoriye materyal ekle: ${sec.title}`}
                  >
                    <span className="tv-icon-btn__duo" aria-hidden="true">
                      <i className="fa-solid fa-play" />
                      <i className="fa-solid fa-plus tv-icon-btn__duo-plus" />
                    </span>
                  </button>
                </div>
              </div>
              <div className="tv-section-body">
                {sec.items.length === 0 ? (
                  <p className="tv-empty-hint">
                    Bu bölümde henüz materyal yok. Bölüm başlığındaki ekle düğmesiyle yükleyebilirsiniz.
                  </p>
                ) : (
                  sec.items.map((v) => (
                    <div key={v.id} className="tv-card">
                      <div>
                        <h3>
                          <i
                            className={`fa-solid ${turBilgisi(v.tur).ikon} tv-card-tur-ikon`}
                            title={turBilgisi(v.tur).ad}
                            aria-hidden="true"
                          />
                          {v.baslik}
                        </h3>
                        <div className="tv-meta">
                          {turBilgisi(v.tur).ad}
                          {v.tur === 'pdf' && v.sayfaSayisi ? ` — ${v.sayfaSayisi} sayfa` : ''}
                          {' · '}
                          Durum: <strong>{v.durum}</strong>
                          {v.durum === 'isleniyor' && ` — %${v.islemYuzdesi ?? 0}`}
                          {v.hataMesaji && ` — ${v.hataMesaji}`}
                        </div>
                        {v.icerik && (
                          <p style={{ fontSize: '0.85rem', color: '#475569', margin: '8px 0 0' }}>
                            {v.icerik.slice(0, 200)}
                            {v.icerik.length > 200 ? '…' : ''}
                          </p>
                        )}
                      </div>
                      <div className="tv-actions">
                        {v.durum === 'hazir' && (
                          <button
                            type="button"
                            className="tv-icon-btn tv-icon-btn--primary tv-icon-btn--sm"
                            onClick={() => openPlay(v.id)}
                            title="Önizle"
                            aria-label={`Önizle: ${v.baslik || v.id}`}
                          >
                            <i
                              className={`fa-solid ${v.tur === 'video' ? 'fa-play' : 'fa-eye'}`}
                              aria-hidden="true"
                            />
                          </button>
                        )}
                        <button
                          type="button"
                          className="tv-icon-btn tv-icon-btn--ghost tv-icon-btn--sm"
                          onClick={() => openEdit(v)}
                          title="Düzenle"
                          aria-label={`Düzenle: ${v.baslik || v.id}`}
                        >
                          <i className="fa-solid fa-pen" aria-hidden="true" />
                        </button>
                        <button
                          type="button"
                          className="tv-icon-btn tv-icon-btn--danger tv-icon-btn--sm"
                          onClick={() => handleDelete(v.id, v.tur)}
                          title="Sil"
                          aria-label={`Sil: ${v.baslik || v.id}`}
                        >
                          <i className="fa-solid fa-trash-can" aria-hidden="true" />
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          ))}
        </>
      )}

      {editOpen && (
        <div
          className="modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="tv-edit-modal-title"
          onClick={closeEdit}
        >
          <div className="modal training-video-modal" role="document" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 id="tv-edit-modal-title">Materyali düzenle</h2>
              <button type="button" className="close-btn" onClick={closeEdit} aria-label="Kapat">
                <i className="fas fa-times" />
              </button>
            </div>
            <div className="modal-body">
              <form onSubmit={submitEdit}>
                <div className="form-grid">
                  <div className="form-field full-width">
                    <span>Başlık</span>
                    <input
                      value={editBaslik}
                      onChange={(e) => setEditBaslik(e.target.value)}
                      required
                      maxLength={300}
                      autoFocus
                    />
                  </div>
                  <div className="form-field full-width">
                    <span>Açıklama</span>
                    <textarea
                      value={editIcerik}
                      onChange={(e) => setEditIcerik(e.target.value)}
                      rows={3}
                      maxLength={20000}
                    />
                  </div>
                  <div className="form-field full-width">
                    <span>Kategori</span>
                    <select value={editKategoriId} onChange={(e) => setEditKategoriId(e.target.value)}>
                      <option value="">Kategorisiz</option>
                      {parentCategoryOptions.map((o) => (
                        <option key={o.id} value={o.id}>
                          {o.label}
                        </option>
                      ))}
                      {editKategoriId && !parentCategoryOptions.some((o) => o.id === editKategoriId) && (
                        <option value={editKategoriId}>
                          {categoryTitleMap[editKategoriId] || editKategoriId}
                        </option>
                      )}
                    </select>
                  </div>
                </div>
                {editError && <div className="tv-page-alert tv-page-alert--error">{editError}</div>}
                <div className="tv-modal-footer">
                  <button type="button" className="secondary-btn" onClick={closeEdit} disabled={editSubmitting}>
                    İptal
                  </button>
                  <button type="submit" className="primary-btn" disabled={editSubmitting}>
                    {editSubmitting ? 'Kaydediliyor…' : 'Güncelle'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {catOpen && (
        <div
          className="modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="tv-category-modal-title"
          onClick={closeCatModal}
        >
          <div className="modal training-video-modal" role="document" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 id="tv-category-modal-title">{catMode === 'create' ? 'Kategori ekle' : 'Kategori düzenle'}</h2>
              <button type="button" className="close-btn" onClick={closeCatModal} aria-label="Kapat">
                <i className="fas fa-times" />
              </button>
            </div>
            <div className="modal-body">
              <form onSubmit={submitCategory}>
                <div className="form-grid">
                  <div className="form-field full-width">
                    <span>Kategori adı</span>
                    <input
                      value={catName}
                      onChange={(e) => setCatName(e.target.value)}
                      required
                      maxLength={100}
                      autoFocus
                      placeholder="Kategori adı"
                    />
                  </div>
                  {catMode === 'create' && (
                    <div className="form-field full-width">
                      <span>Üst kategori</span>
                      <select value={catParentId} onChange={(e) => setCatParentId(e.target.value)}>
                        <option value="">Yok</option>
                        {parentCategoryOptions.map((o) => (
                          <option key={o.id} value={o.id}>
                            {o.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
                {catError && <div className="tv-page-alert tv-page-alert--error">{catError}</div>}
                <div className="tv-modal-footer">
                  <button type="button" className="secondary-btn" onClick={closeCatModal} disabled={catSubmitting}>
                    İptal
                  </button>
                  <button type="submit" className="primary-btn" disabled={catSubmitting}>
                    {catSubmitting ? 'Kaydediliyor…' : catMode === 'create' ? 'Ekle' : 'Güncelle'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {addOpen && (
        <div
          className="modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="tv-upload-modal-title"
          onClick={closeAdd}
        >
          <div className="modal training-video-modal" role="document" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <h2 id="tv-upload-modal-title">Materyal yükle</h2>
                <p className="tv-modal-sub">Hedef: {selectedCategoryLabel}</p>
              </div>
              <button type="button" className="close-btn" onClick={closeAdd} aria-label="Kapat">
                <i className="fas fa-times" />
              </button>
            </div>
            <div className="modal-body">
              {phase === 'upload' && (
                <div className="tv-modal-progress-card">
                  <h3>Dosya gönderiliyor</h3>
                  <div className="tv-modal-progress-pct">%{uploadPct}</div>
                  <div className="upload-progress">
                    <div className="upload-progress-bar" style={{ width: `${uploadPct}%` }} />
                  </div>
                  <p className="tv-file-drop-hint">İsterseniz bu pencereyi kapatmayın.</p>
                </div>
              )}
              {phase === 'transcode' && (
                <div className="tv-modal-progress-card">
                  <h3>{turBilgisi(secilenTur).hazirlikBaslik}</h3>
                  <div className="tv-modal-progress-pct">%{transPct}</div>
                  <div className="upload-progress">
                    <div className="upload-progress-bar" style={{ width: `${transPct}%` }} />
                  </div>
                  <p className="tv-file-drop-hint">Bittiğinde liste otomatik yenilenecek.</p>
                </div>
              )}
              {phase === 'done' && (
                <div className="tv-modal-progress-card">
                  <p style={{ color: '#15803d', margin: '0 0 16px', fontWeight: 600 }}>
                    {turBilgisi(secilenTur).ad} hazır.
                  </p>
                  <button type="button" className="primary-btn" onClick={() => setAddOpen(false)}>
                    Tamam
                  </button>
                </div>
              )}
              {!phase && (
                <form onSubmit={submitUpload}>
                  <div className="form-grid">
                    <div className="form-field full-width">
                      <span>Başlık</span>
                      <input value={baslik} onChange={(e) => setBaslik(e.target.value)} required placeholder="Kısa başlık" />
                    </div>
                    <div className="form-field full-width">
                      <span>Açıklama / içerik metni</span>
                      <textarea value={icerik} onChange={(e) => setIcerik(e.target.value)} rows={3} placeholder="İsteğe bağlı" />
                    </div>
                    <div className="form-field full-width">
                      <span>Kategori</span>
                      <select value={addKategoriId} onChange={(e) => setAddKategoriId(e.target.value)}>
                        <option value="">Kategorisiz</option>
                        {flatCategoryOptions.map((o) => (
                          <option key={o.id} value={o.id}>
                            {o.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="form-field full-width">
                      <span>Dosya</span>
                      <div className="tv-file-drop">
                        <input
                          type="file"
                          accept="video/*,application/pdf,image/jpeg,image/png,image/webp"
                          onChange={(e) => setFile(e.target.files?.[0] || null)}
                          required
                        />
                        <p className="tv-file-drop-hint">
                          Video (en fazla 800MB), PDF (60MB) veya görsel (20MB). Sunucu videoyu HLS'e,
                          PDF'i sayfa görsellerine dönüştürür.
                        </p>
                        {file && (
                          <div className="tv-file-name">
                            <i
                              className={`fa-solid ${turBilgisi(secilenTur).ikon}`}
                              aria-hidden="true"
                            />{' '}
                            {file.name}
                            {!secilenTur && ' — desteklenmeyen tür'}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <details className="tv-tags-details">
                    <summary>İçerik etiketleri (ürün, parça, işlem, arıza)</summary>
                    <div className="tag-picker-wrap">
                      <ContentTagPicker
                        icerikTipi="urun"
                        label="Ürün etiketi"
                        value={tagUrun.id}
                        displayName={tagUrun.ad}
                        onChange={(id, ad) => setTagUrun({ id, ad })}
                      />
                      <ContentTagPicker
                        icerikTipi="parca"
                        label="Parça etiketi"
                        value={tagParca.id}
                        displayName={tagParca.ad}
                        onChange={(id, ad) => setTagParca({ id, ad })}
                      />
                      <ContentTagPicker
                        icerikTipi="islem"
                        label="İşlem etiketi"
                        value={tagIslem.id}
                        displayName={tagIslem.ad}
                        onChange={(id, ad) => setTagIslem({ id, ad })}
                      />
                      <ContentTagPicker
                        icerikTipi="ariza"
                        label="Arıza etiketi"
                        value={tagAriza.id}
                        displayName={tagAriza.ad}
                        onChange={(id, ad) => setTagAriza({ id, ad })}
                      />
                    </div>
                  </details>

                  <div className="tv-modal-footer">
                    <button type="button" className="secondary-btn" onClick={closeAdd}>
                      İptal
                    </button>
                    <button type="submit" className="primary-btn" disabled={!file || !secilenTur}>
                      Yükle ve hazırla
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}

      {playOpen && (
        <div
          className="modal-overlay"
          role="dialog"
          aria-modal="true"
          onClick={() => setPlayOpen(false)}
        >
          <div className="modal training-video-play-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Önizleme</h2>
              <button type="button" className="close-btn" onClick={() => setPlayOpen(false)} aria-label="Kapat">
                <i className="fas fa-times" />
              </button>
            </div>
            {playTur === 'video' && (
              <div className="play-modal-video-wrap">
                <video ref={videoRef} controls playsInline />
              </div>
            )}
            {playTur === 'pdf' && (
              <div className="tv-doc-preview">
                {playSayfalar.map((src, i) => (
                  <img key={src} src={src} alt={`Sayfa ${i + 1}`} className="tv-doc-page" />
                ))}
              </div>
            )}
            {playTur === 'gorsel' && (
              <div className="tv-doc-preview">
                <img src={playUrl} alt="Eğitim görseli" className="tv-doc-page" />
              </div>
            )}
          </div>
        </div>
      )}
    </main>
  );
}

export default TrainingVideosPage;
